import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import DiscussionList from 'flarum/forum/components/DiscussionList';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';
import IndexPage from 'flarum/forum/components/IndexPage';
import CommentPost from 'flarum/forum/components/CommentPost';
import PostUser from 'flarum/forum/components/PostUser';
import Avatar from 'flarum/common/components/Avatar';
import Link from 'flarum/common/components/Link';
import humanTime from 'flarum/common/utils/humanTime';
import abbreviateNumber from 'flarum/common/utils/abbreviateNumber';
import listItems from 'flarum/common/helpers/listItems';
import { childTagsOf, isChildTag, tagFromListState, tagHref } from './ChildTagDiscussionSections';
function trans(key) {
  return app.translator.trans(`itagora-theme.forum.${key}`);
}
function viewCountOf(discussion) {
  if (!discussion) {
    return null;
  }
  const names = ['viewCount', 'views', 'viewsCount', 'view_count'];
  for (const name of names) {
    if (typeof discussion[name] === 'function') {
      const value = discussion[name]();
      if (typeof value === 'number') {
        return value;
      }
    }
  }
  for (const name of names) {
    const value = discussion.attribute?.(name);
    if (typeof value === 'number') {
      return value;
    }
  }
  return null;
}
function statCell(className, label) {
  return m(`span.DiscussionListItem-stats-item.${className}`, m('span.DiscussionListItem-stats-item-label', label));
}
function pageDiscussions(page) {
  const items = page && page.items;
  if (!items) {
    return [];
  }
  let list = [];
  if (Array.isArray(items)) {
    list = items;
  } else if (typeof items.toArray === 'function') {
    list = items.toArray();
  } else if (typeof items.map === 'function') {
    list = Array.from(items);
  }
  return list.filter(item => item && typeof item.id === 'function');
}
function discussionTagIds(discussion) {
  try {
    const raw = typeof discussion.tags === 'function' ? discussion.tags() : null;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.filter(Boolean).map(tag => tag.id());
  } catch (e) {
    return [];
  }
}
function vnodeClass(node) {
  if (!node || typeof node !== 'object' || !node.attrs) {
    return '';
  }
  const value = node.attrs.className || node.attrs.class;
  return typeof value === 'string' ? value : '';
}
function normalizeChildren(children) {
  if (children == null) {
    return [];
  }
  return Array.isArray(children) ? children : [children];
}
function insertAfter(parent, target, insert) {
  const kids = normalizeChildren(parent.children).slice();
  const idx = kids.indexOf(target);
  if (idx >= 0) {
    kids.splice(idx + 1, 0, insert);
    parent.children = kids;
    return;
  }
  for (let i = 0; i < kids.length; i++) {
    if (!Array.isArray(kids[i])) {
      continue;
    }
    const nestedIdx = kids[i].indexOf(target);
    if (nestedIdx >= 0) {
      const nested = kids[i].slice();
      nested.splice(nestedIdx + 1, 0, insert);
      kids[i] = nested;
      parent.children = kids;
      return;
    }
  }
  kids.push(insert);
  parent.children = kids;
}
function flattenChildren(children) {
  const out = [];
  normalizeChildren(children).forEach(child => {
    if (Array.isArray(child)) {
      flattenChildren(child).forEach(nested => out.push(nested));
    } else {
      out.push(child);
    }
  });
  return out;
}
function findDiscussionsUl(vnode, parent, index) {
  if (!vnode || typeof vnode !== 'object') {
    return null;
  }
  if (vnodeClass(vnode).includes('DiscussionList-discussions')) {
    return {
      node: vnode,
      parent,
      index
    };
  }
  const kids = flattenChildren(vnode.children);
  for (let i = 0; i < kids.length; i++) {
    const found = findDiscussionsUl(kids[i], vnode, i);
    if (found) {
      return found;
    }
  }
  return null;
}
function expandParentTagFilter(params) {
  if (!params || !this || !this.params || !this.params.tags || this.params.q) {
    return;
  }
  const slug = this.params.tags;
  const tag = tagFromListState(this);
  if (!tag || isChildTag(tag)) {
    return;
  }
  const children = childTagsOf(tag);
  if (!children.length) {
    return;
  }
  const slugs = [slug];
  children.forEach(child => {
    if (typeof child.slug === 'function' && child.slug()) {
      slugs.push(child.slug());
    }
  });
  const unique = slugs.filter((item, index) => slugs.indexOf(item) === index);
  if (unique.length < 2) {
    return;
  }
  params.filter = params.filter || {};
  params.filter.tag = unique.join(',');
}
function regroupDiscussionList(list, vnode) {
  const state = list.attrs && list.attrs.state;
  if (!state || !state.params || !state.params.tags) {
    return;
  }
  const tag = tagFromListState(state);
  if (!tag || isChildTag(tag)) {
    return;
  }
  const children = childTagsOf(tag);
  if (!children.length) {
    return;
  }
  const all = [];
  (state.getPages() || []).forEach(page => {
    pageDiscussions(page).forEach(discussion => all.push(discussion));
  });
  if (!all.length) {
    return;
  }
  const parentId = tag.id();
  const parentDiscussions = [];
  const byChild = {};
  children.forEach(child => {
    byChild[child.id()] = [];
  });
  all.forEach(discussion => {
    const ids = discussionTagIds(discussion);
    if (ids.indexOf(parentId) !== -1) {
      parentDiscussions.push(discussion);
      return;
    }
    const match = children.find(child => ids.indexOf(child.id()) !== -1);
    if (match) {
      byChild[match.id()].push(discussion);
    } else {
      parentDiscussions.push(discussion);
    }
  });
  const params = typeof state.getParams === 'function' ? state.getParams() : {};
  const childSections = [];
  children.forEach(child => {
    const items = byChild[child.id()] || [];
    if (!items.length) {
      return;
    }
    const color = typeof child.color === 'function' ? child.color() : null;
    const icon = typeof child.icon === 'function' ? child.icon() : null;
    const description = typeof child.description === 'function' ? child.description() : null;
    childSections.push(m('section.itagora-child-discussions-section', {
      key: 'child-' + child.id()
    }, [m(Link, {
      className: 'itagora-child-discussions-heading',
      href: tagHref(child),
      style: color ? {
        '--itagora-tag-color': color
      } : undefined
    }, [m('span.itagora-child-discussions-heading-main', [icon ? m('i.icon', {
      className: icon
    }) : null, m('span.itagora-child-discussions-name', child.name())]), description ? m('span.itagora-child-discussions-desc', description) : null, m('span.itagora-child-discussions-more', trans('children.see_all'))]), m('ul.DiscussionList-discussions', items.map(discussion => m('li', {
      key: discussion.id(),
      'data-id': discussion.id(),
      role: 'article'
    }, m(DiscussionListItem, {
      discussion,
      params
    }))))]));
  });
  if (!childSections.length) {
    return;
  }
  const found = findDiscussionsUl(vnode, null, 0);
  if (!found) {
    return;
  }
  const parentLis = parentDiscussions.map(discussion => m('li', {
    key: discussion.id(),
    'data-id': discussion.id(),
    role: 'article'
  }, m(DiscussionListItem, {
    discussion,
    params
  })));
  const sections = m('.itagora-child-discussions', {
    key: 'itagora-children-' + tag.id()
  }, childSections);
  found.node.children = parentLis;
  if (!found.parent) {
    const original = {
      tag: vnode.tag,
      attrs: vnode.attrs,
      children: vnode.children
    };
    vnode.tag = 'div';
    vnode.attrs = {
      className: 'DiscussionList'
    };
    vnode.children = [original, sections];
    return;
  }
  insertAfter(found.parent, found.node, sections);
}
app.initializers.add('itagora-theme', () => {
  document.documentElement.classList.add('itagora-theme');
  document.documentElement.setAttribute('data-itagora-rev', '2026-09-14a');
  extend(IndexPage.prototype, 'toolbarItems', function (items) {
    try {
      const tag = typeof app.currentTag === 'function' ? app.currentTag() : null;
      if (!tag || !items || typeof items.add !== 'function') {
        return;
      }
      const color = typeof tag.color === 'function' ? tag.color() : null;
      const icon = typeof tag.icon === 'function' ? tag.icon() : null;
      const description = typeof tag.description === 'function' ? tag.description() : null;
      items.add('itagoraTagHeading', m('.IndexPage-toolbar-tag', color ? {
        style: {
          '--itagora-tag-color': color
        }
      } : undefined, [m('h2.IndexPage-toolbar-tagTitle', [icon ? m('i.icon', {
        className: icon
      }) : null, icon ? ' ' : null, tag.name()]), description ? m('p.IndexPage-toolbar-tagDescription', description) : null]), 95);
    } catch (e) {
      console.error('[itagora-theme] toolbar', e);
    }
  });
  extend(DiscussionListState.prototype, 'mutateRequestParams', function (params) {
    try {
      expandParentTagFilter.call(this, params);
    } catch (e) {
      console.error('[itagora-theme] tag filter', e);
    }
  });
  extend(DiscussionList.prototype, 'view', function (vnode) {
    if (!vnode) {
      return;
    }
    if (!Array.isArray(vnode.children)) {
      vnode.children = vnode.children != null ? [vnode.children] : [];
    }
    if (!findDiscussionsUl(vnode, null, 0)) {
      return;
    }
    const first = pageDiscussions(this.attrs.state?.getPages?.()[0] || {})[0];
    if (viewCountOf(first) !== null) {
      document.body.classList.add('itagora-has-views');
    }
    vnode.children.unshift(m('.DiscussionList-head', [m('span.DiscussionList-head-topic', trans('list.topic')), m('span.DiscussionList-head-poster'), m('span.DiscussionList-head-replies', trans('list.replies')), m('span.DiscussionList-head-views', trans('list.views')), m('span.DiscussionList-head-activity', trans('list.activity'))]));
    try {
      regroupDiscussionList(this, vnode);
    } catch (e) {
      console.error('[itagora-theme] regroup', e);
    }
  });
  if (typeof DiscussionListItem.prototype.elementAttrs === 'function') {
    extend(DiscussionListItem.prototype, 'elementAttrs', function (attrs) {
      try {
        const discussion = this.attrs.discussion;
        if (!discussion || !attrs) {
          return;
        }
        const classes = [attrs.className || ''];
        if (typeof discussion.isSticky === 'function' && discussion.isSticky()) {
          classes.push('DiscussionListItem--sticky');
        }
        if (typeof discussion.isLocked === 'function' && discussion.isLocked()) {
          classes.push('DiscussionListItem--locked');
        }
        attrs.className = classes.join(' ').trim();
      } catch (e) {
        console.error('[itagora-theme] list badges class', e);
      }
    });
  }
  if (typeof DiscussionListItem.prototype.authorItems === 'function') {
    extend(DiscussionListItem.prototype, 'authorItems', function (items) {
      if (items && typeof items.remove === 'function' && items.has('badges')) {
        items.remove('badges');
      }
    });
  }
  extend(DiscussionListItem.prototype, 'contentItems', function (items) {
    const discussion = this.attrs.discussion;
    const user = typeof discussion.lastPostedUser === 'function' && discussion.lastPostedUser() || discussion.user();
    if (items && typeof items.remove === 'function' && items.has('badges')) {
      items.remove('badges');
    }
    items.add('lastPoster', m('.DiscussionListItem-lastPoster', user ? m(Link, {
      href: app.route.user(user)
    }, m(Avatar, {
      user
    })) : m(Avatar, {
      user: null
    })), 75);
  });
  if (typeof DiscussionListItem.prototype.mainView === 'function') {
    extend(DiscussionListItem.prototype, 'mainView', function (vnode) {
      try {
        if (!vnode) {
          return;
        }
        const discussion = this.attrs.discussion;
        const list = discussion && typeof discussion.badges === 'function' ? discussion.badges() : null;
        const badges = list && typeof list.toArray === 'function' ? list.toArray() : [];
        if (!badges.length) {
          return;
        }
        if (!Array.isArray(vnode.children)) {
          vnode.children = vnode.children != null ? [vnode.children] : [];
        }
        vnode.children.unshift(m('ul.DiscussionListItem-badges.badges', listItems(badges)));
      } catch (e) {
        console.error('[itagora-theme] list badges', e);
      }
    });
  }
  extend(DiscussionListItem.prototype, 'statsItems', function (items) {
    const discussion = this.attrs.discussion;
    const views = viewCountOf(discussion);
    const lastPostedAt = typeof discussion.lastPostedAt === 'function' && discussion.lastPostedAt() || discussion.createdAt();
    if (items.has('replyCount')) {
      const reply = items.get('replyCount');
      if (reply?.attrs) {
        reply.attrs.className = `${reply.attrs.className || ''} DiscussionListItem-stats-item--replies`.trim();
      }
    }
    if (views !== null) {
      items.add('views', statCell('DiscussionListItem-stats-item--views', abbreviateNumber(views)), 60);
    }
    items.add('activity', statCell('DiscussionListItem-stats-item--activity', lastPostedAt ? humanTime(lastPostedAt) : '—'), 50);
  });
  extend(CommentPost.prototype, 'sideItems', function (items) {
    items.add('userInfo', m(PostUser, {
      post: this.attrs.post
    }), 80);
  });
  extend(PostUser.prototype, 'userViewItems', function (items, user) {
    if (!user) {
      return;
    }
    const joined = typeof user.joinTime === 'function' ? user.joinTime() : null;
    const posts = typeof user.commentCount === 'function' ? user.commentCount() : null;
    const joinedLabel = joined ? joined.toLocaleDateString(undefined, {
      month: 'short',
      year: 'numeric'
    }) : '—';
    items.add('itagoraStats', m('ul.PostUser-stats', [posts != null ? m('li', [m('span', trans('post.posts')), m('strong', abbreviateNumber(posts))]) : null, m('li', [m('span', trans('post.joined')), m('strong', joinedLabel)])]), 70);
  });
});